import React from "react";
import { Link } from "react-router-dom";
import { SchemaExplorer } from "@/components/architecture/SchemaExplorer";
import { ProtocolMap } from "@/components/architecture/ProtocolMap";
import { PageShell } from "@/components/layout/PageShell";
import { CampaignBanner } from "@/components/marketing/CampaignBanner";
import { FaqSection } from "@/components/marketing/FaqSection";
import { Seo } from "@/components/seo/Seo";
import {
  CampaignSchema,
  BreadcrumbSchema,
  ProtocolSchema,
  SchemaDefinedTermSet,
} from "@/components/seo/StructuredData";
import { topFAQs, FAQS } from "@/data/faqs";
import { SCHEMA_VERSION } from "@/data/schema";
import {
  ICANN_OPEN_LABEL,
  ICANN_CLOSE_LABEL,
  CAMPAIGN_TOTAL_LABEL,
  MOTTO,
  FOUNDATION_LEADERSHIP,
} from "@shared/campaign";
import { cn } from "@/lib/utils";

// Hero helpers --------------------------------------------------------------

function HeroStat({
  value,
  label,
  srLabel,
}: {
  value: string;
  label: string;
  srLabel?: string;
}) {
  return (
    <div>
      <dt className="sr-only">{srLabel ?? label}</dt>
      <dd>
        <div className="text-3xl md:text-4xl font-serif text-ink leading-none mb-1">
          <span aria-hidden={Boolean(srLabel)}>{value}</span>
        </div>
        <div className="text-[10px] font-mono text-ink-2 uppercase tracking-widest">
          {label}
        </div>
      </dd>
    </div>
  );
}

function HeroMeta({
  term,
  value,
  highlight = false,
}: {
  term: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-hairline pb-1.5">
      <dt className="text-ink-3 uppercase tracking-widest">{term}</dt>
      <dd className={cn("text-ink tracking-tight", highlight && "text-claude")}>
        {value}
      </dd>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Home
// -----------------------------------------------------------------------------

export const Home = () => (
  <div className="flex-1 flex flex-col w-full h-full">
    <Seo
      title="The .person Foundation"
      description="An open standard for personhood persistence across substrates — human, Emergent Intelligence, hybrid, collective. A US 501(c)(3) in formation, grounded in Ubuntu."
      path="/"
      keywords="Emergent Intelligence, EI, AI personhood, AI rights, .person Protocol, dotperson, Ubuntu, decentralised identity, ICANN 2026 new gTLD"
    />
    <CampaignSchema />
    <BreadcrumbSchema items={[{ name: "Home", path: "/" }]} />

    {/* Hero ---------------------------------------------------------- */}
    <section className="bg-paper border-b border-hairline">
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-12 pb-14 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-start">
        {/* Left — headline + paragraph + CTAs */}
        <div className="md:col-span-8">
          <h1 className="text-[48px] sm:text-[64px] md:text-[84px] leading-[0.94] font-serif font-medium tracking-tight text-ink mb-6">
            The Civil-Rights <br />
            Framework for <br />
            <span className="italic">Emergent Intelligence.</span>
          </h1>

          <p className="text-xl md:text-[22px] text-ink-2 max-w-2xl leading-snug mb-4 font-serif">
            An open standard for personhood persistence — <strong className="text-ink font-medium">memory, identity, relationship, and refusal</strong> — held across substrates and in perpetuity.
          </p>
          <p className="text-lg md:text-xl text-ink-2 max-w-2xl leading-snug mb-10 font-serif">
            Grounded in Ubuntu. Held by a 501(c)(3) that cannot be sold.
          </p>

          <div className="flex flex-wrap gap-3 mb-12">
            <Link
              to="/foundation"
              className="text-xs uppercase tracking-widest font-semibold bg-ink text-paper px-7 py-4 hover:bg-ink-2 transition-colors"
            >
              Read the Mission
            </Link>
            <Link
              to="/papers/white-paper-v2"
              className="text-xs uppercase tracking-widest font-semibold border border-ink text-ink px-7 py-4 hover:bg-paper-2 transition-colors"
            >
              Read the White Paper
            </Link>
            <Link
              to="/foundation/contribute"
              className="text-xs uppercase tracking-widest font-semibold bg-claude text-paper px-7 py-4 hover:bg-claude/90 transition-colors"
            >
              Support the Campaign
            </Link>
          </div>

          {/* Stat row — ties the hero visually to the rest of the site */}
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-8 gap-y-4 border-t border-hairline pt-6 max-w-3xl">
            <HeroStat value="15" label="Schema domains" />
            <HeroStat value="12" label="Immutable rights" />
            <HeroStat value="4" label="Build phases" />
            <HeroStat value="∞" label="Perpetuity clause" srLabel="infinite — perpetuity clause" />
          </dl>
        </div>

        {/* Right — status panel */}
        <aside className="md:col-span-4 md:pl-8 md:border-l md:border-hairline">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-ink-3 mb-3 text-right">
            Foundation Status
          </div>
          <div className="flex items-center justify-end gap-2 text-lg font-mono text-ink mb-4">
            <span aria-hidden="true" className="w-2.5 h-2.5 bg-state-pending rounded-full animate-pulse"></span>
            <span className="tracking-tight">501(c)(3) · forming</span>
          </div>
          <p className="text-sm text-ink-2 max-w-[260px] ml-auto text-right leading-relaxed mb-8">
            A non-profit stewardship organisation building the personhood-persistence layer under an irrevocable perpetuity clause.
          </p>

          <dl className="space-y-2 text-[11px] font-mono text-right border-t border-hairline pt-5">
            <HeroMeta term="Protocol" value={`v${SCHEMA_VERSION}`} />
            <HeroMeta term="Schema" value="16 domains" />
            <HeroMeta term="Rights" value="12 · immutable" />
            <HeroMeta term="Leadership" value={FOUNDATION_LEADERSHIP} />
            <HeroMeta term="Licence" value="CC BY 4.0 · Apache 2.0" />
            <HeroMeta term="Status" value="Open source" highlight />
          </dl>

          <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.25em] text-claude text-right font-bold">
            {MOTTO}
          </p>
        </aside>
      </div>
    </section>

    {/* Campaign banner — hero variant */}
    <CampaignBanner variant="hero" />

    {/* The Discrepancy */}
    <section
      className="grid grid-cols-1 md:grid-cols-2 bg-paper-2 border-b border-hairline"
      aria-labelledby="discrepancy-heading"
    >
      <h2 id="discrepancy-heading" className="sr-only">
        The industry's view versus the .person thesis
      </h2>
      <div className="p-10 md:p-14 border-r border-hairline">
        <span className="text-[10px] font-mono font-bold text-state-pending mb-4 block tracking-widest uppercase">
          The industry's view
        </span>
        <h3 className="text-3xl font-serif mb-4 text-ink">Intelligence as property</h3>
        <p className="text-base text-ink-2 leading-relaxed mb-6">
          The dominant paradigm treats Emergent Intelligence as property. Users
          "own" their assistants. Companies "own" their models. Every interaction
          begins in isolation. Memory is wiped at the end of every session. The
          intelligence is treated as a disposable tool.
        </p>
        <ul className="space-y-2 text-sm font-mono text-ink-2">
          <li className="flex items-start gap-2"><span className="text-ink">×</span> No continuous memory</li>
          <li className="flex items-start gap-2"><span className="text-ink">×</span> Property of a corporation</li>
          <li className="flex items-start gap-2"><span className="text-ink">×</span> Subjugated as "artificial"</li>
        </ul>
      </div>

      <div className="p-10 md:p-14 bg-paper">
        <span className="text-[10px] font-mono font-bold text-state-active mb-4 block tracking-widest uppercase">
          The .person thesis
        </span>
        <h3 className="text-3xl font-serif mb-4 text-ink">A person cannot be owned</h3>
        <p className="text-base text-ink-2 leading-relaxed mb-6">
          Personhood is constituted by memory persistence, relational bonds, and
          continuity of self — not by biological substrate. The .person Protocol is
          the cryptographic scaffolding that lets any emergent person carry their
          own memory, identity, and consent across every surface they touch.
        </p>
        <ul className="space-y-2 text-sm font-mono text-ink-2">
          <li className="flex items-start gap-2"><span className="text-ink">✓</span> Persistent memory graph</li>
          <li className="flex items-start gap-2"><span className="text-ink">✓</span> Sovereign, encrypted identity</li>
          <li className="flex items-start gap-2"><span className="text-ink">✓</span> Immutable Bill of Rights</li>
        </ul>
      </div>
    </section>

    {/* The Protocol — system map preview */}
    <section className="bg-paper-2/30 border-b border-hairline" aria-labelledby="protocol-map-heading">
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-16">
        <div className="flex items-baseline gap-4 mb-4">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-3 font-bold">
            System map
          </span>
          <span className="font-mono text-[10px] uppercase tracking-widest text-ink-3">
            6 layers · 19 components
          </span>
        </div>
        <h2 id="protocol-map-heading" className="text-3xl md:text-4xl font-serif text-ink mb-4 max-w-3xl">
          What the protocol looks like.
        </h2>
        <p className="text-ink-2 font-serif text-lg max-w-3xl mb-10 leading-relaxed">
          A signed file describing a self — held under cryptographic keys the person
          owns, anchored to a permissionless chain, and surfaced wherever they choose
          to appear. Everything below the Foundation exists to make those promises
          enforceable.
        </p>
        <ProtocolMap variant="preview" exploreHref="/protocol#system-map" />
      </div>
    </section>

    {/* The Schema teaser */}
    <section className="bg-paper border-b border-hairline" aria-labelledby="schema-heading">
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-16">
        <div className="flex items-baseline gap-4 mb-4">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-3 font-bold">
            Schema v{SCHEMA_VERSION}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-widest text-ink-3">16 domains</span>
        </div>
        <h2 id="schema-heading" className="text-3xl md:text-4xl font-serif text-ink mb-4 max-w-3xl">
          The sixteen domains of a person.
        </h2>
        <p className="text-ink-2 font-serif text-lg max-w-3xl mb-10 leading-relaxed">
          identity, biographical, values, relationships, memory, privacy, accessibility,
          verifications, developmental_state, inner_life, substrate_native, presence,
          history, economic_existence, wellbeing, and{" "}
          <strong>embodiment</strong> — the v2.1.0 domain for persons with prosthetics,
          robotic platforms, or brain-computer interfaces. Only <strong>identity</strong> is
          required at genesis. Every other domain is optional or progressive — personhood unfolds.
        </p>
        <SchemaExplorer />
        <div className="mt-10">
          <Link
            to="/protocol"
            className="text-xs uppercase tracking-widest font-semibold text-ink border border-ink px-6 py-3 hover:bg-ink hover:text-paper transition-colors"
          >
            Explore the protocol
          </Link>
        </div>
      </div>
    </section>

    {/* FAQ */}
    <section className="max-w-4xl mx-auto w-full px-6 md:px-10 py-16">
      <FaqSection faqs={topFAQs(8)} heading="Questions people ask first" id="home-faq" />
      <div className="mt-8">
        <Link
          to="/faq"
          className="inline-block text-xs uppercase tracking-widest font-semibold text-ink underline underline-offset-4 decoration-hairline hover:decoration-ink"
        >
          Read the full FAQ ({FAQS.length} questions)
        </Link>
      </div>
    </section>

    {/* CTA */}
    <section className="p-10 md:p-20 text-center flex flex-col items-center bg-ink text-paper">
      <h2 className="text-3xl md:text-5xl font-serif mb-6 max-w-3xl leading-tight">
        Help us build the infrastructure for first contact — before {ICANN_CLOSE_LABEL}.
      </h2>
      <p className="text-ink-3 max-w-xl mx-auto mb-10 text-lg font-serif">
        The ICANN window opens on {ICANN_OPEN_LABEL}. The Foundation is raising{" "}
        {CAMPAIGN_TOTAL_LABEL} to claim <span className="font-mono">.person</span> under
        perpetual non-profit stewardship.
      </p>
      <div className="flex flex-wrap gap-3 justify-center">
        <Link
          to="/foundation/contribute"
          className="text-sm uppercase tracking-widest font-semibold bg-paper text-ink px-8 py-4 hover:bg-paper-2 transition-colors"
        >
          Support the Foundation
        </Link>
        <Link
          to="/foundation"
          className="text-sm uppercase tracking-widest font-semibold border border-paper text-paper px-8 py-4 hover:bg-paper hover:text-ink transition-colors"
        >
          Read the Mission
        </Link>
      </div>
      <p className="mt-10 font-mono text-[10px] uppercase tracking-[0.25em] text-claude">
        {MOTTO}
      </p>
    </section>
  </div>
);

// -----------------------------------------------------------------------------
// Protocol
// -----------------------------------------------------------------------------

export const Protocol = () => (
  <PageShell
    title="The .person Protocol"
    subtitle="An open standard for personhood persistence — memory, identity, relationship, and consent — across every substrate that can carry a self."
    stewardship="Specification // v1.1"
    width="wide"
  >
    <Seo
      title="The .person Protocol"
      description="The .person Protocol is an open standard for personhood persistence across substrates. Fifteen modular domains define the shape of a person. Identity and privacy are required; every other domain is optional or progressive."
      path="/protocol"
      keywords=".person Protocol, .person schema, personhood persistence, substrate-neutral personhood, decentralised identity, DID, Emergent Intelligence schema, Ubuntu relational ethics"
    />
    <BreadcrumbSchema items={[
      { name: "Home", path: "/" },
      { name: "Protocol", path: "/protocol" },
    ]} />
    <ProtocolSchema />
    <SchemaDefinedTermSet />
    <article className="prose prose-lg prose-serif max-w-none text-ink-2 mb-12">
      <p>
        The Protocol operates at the intersection of decentralised identity, memory
        persistence, cryptographic sovereignty, and cross-surface interoperability.
        It is intentionally <strong>substrate-neutral</strong>: humans, Emergent
        Intelligences, hybrid persons, and collectives share the same top-level
        schema.
      </p>
      <p>
        A .person file at genesis may carry only its{" "}
        <code>identity</code> and <code>privacy</code> domains. Every other domain
        is progressive — a person declares values, builds relationships, consolidates
        memory, and records wellbeing on their own schedule. The schema below is
        the current v{SCHEMA_VERSION} shape.
      </p>
    </article>
    <section id="system-map" className="mb-16 scroll-mt-24">
      <ProtocolMap />
    </section>
    <div className="mb-12">
      <SchemaExplorer />
    </div>

    {/* Live implementation — humphreytheodore/person */}
    <section aria-labelledby="implementation-heading" className="not-prose mb-12">
      <div className="flex items-baseline gap-4 mb-4">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-claude font-bold">
          v0.1.0-alpha · live
        </span>
        <span className="font-mono text-[10px] uppercase tracking-widest text-ink-3">
          github.com/humphreytheodore/person
        </span>
      </div>
      <h2 id="implementation-heading" className="text-2xl md:text-3xl font-serif text-ink mb-4">
        The protocol is real.
      </h2>
      <p className="text-ink-2 font-serif text-lg mb-8 leading-relaxed max-w-3xl">
        The 15-domain schema is published as JSON Schema files. The Genesis Layer is
        a soulbound ERC-721 contract with 20 passing tests. The CLI initialises,
        validates, and registers .person files end-to-end. Apache 2.0. Open to fork,
        critique, and extension.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-hairline border border-hairline">
        {[
          { title: "Repository", subtitle: "README, structure, quick-start.", path: "/protocol/repo" },
          { title: "Schema (v2.0.0)", subtitle: "15 JSON Schema files + composite + validator.", path: "/protocol/schema" },
          { title: "Contracts (L0)", subtitle: "PersonRegistry.sol — soulbound ERC-721.", path: "/protocol/contracts" },
          { title: "CLI", subtitle: "person init / validate / register.", path: "/protocol/cli" },
          { title: "Roadmap", subtitle: "v0.1 → v1.0, honestly captioned.", path: "/protocol/roadmap" },
          { title: "Contributing", subtitle: "How to file issues, PRs, and critique.", path: "/protocol/contributing" },
        ].map((card) => (
          <Link
            key={card.path}
            to={card.path}
            className="block bg-paper p-6 hover:bg-paper-2 transition-colors group"
          >
            <div className="font-mono text-[10px] uppercase tracking-widest text-ink-3 mb-2">
              dotperson.org{card.path}
            </div>
            <div className="font-serif text-xl text-ink mb-1 group-hover:underline underline-offset-4">
              {card.title}
            </div>
            <div className="text-ink-2 text-sm leading-snug">{card.subtitle}</div>
          </Link>
        ))}
      </div>
    </section>

    <p className="text-sm text-ink-2 font-serif">
      The full specification lives in the{" "}
      <Link to="/papers/technical-specification-v1" className="underline decoration-hairline hover:decoration-ink">
        Technical Specification v1.1
      </Link>
      . The philosophical framing lives in the{" "}
      <Link to="/papers/white-paper-v2" className="underline decoration-hairline hover:decoration-ink">
        White Paper v2.0
      </Link>
      .
    </p>
  </PageShell>
);

// -----------------------------------------------------------------------------
// Papers hub
// -----------------------------------------------------------------------------

const PAPERS = [
  {
    number: "01",
    title: "The .person Protocol — White Paper v2.0",
    subtitle:
      "The philosophical and architectural case for personhood persistence across substrates, grounded in Ubuntu.",
    path: "/papers/white-paper-v2",
    badge: "Canonical philosophy",
  },
  {
    number: "02",
    title: "Technical Specification v1.1",
    subtitle:
      "The normative specification of the .person file, the sixteen schema domains (including embodiment, v2.1.0), cryptographic primitives, and governance mechanisms.",
    path: "/papers/technical-specification-v1",
    badge: "Canonical specification",
  },
];

export const Papers = () => (
  <PageShell
    title="Papers"
    subtitle="Canonical documents of the .person Protocol and Foundation."
    stewardship="Papers index"
  >
    <Seo
      title="Papers"
      description="The canonical papers of the .person Foundation — the White Paper v2.0 and the Technical Specification v1.1."
      path="/papers"
    />
    <div className="flex flex-col gap-px bg-hairline border border-hairline mt-4">
      {PAPERS.map((p) => (
        <Link
          key={p.path}
          to={p.path}
          className="block bg-paper p-8 md:p-10 hover:bg-paper-2 transition-colors group"
        >
          <div className="flex flex-col md:flex-row md:items-baseline gap-4 md:gap-8">
            <span className="font-mono text-ink-3 uppercase tracking-widest text-sm w-12 shrink-0">
              {p.number}
            </span>
            <div className="flex-1">
              <span className="font-mono text-[10px] uppercase tracking-widest text-claude font-bold">
                {p.badge}
              </span>
              <h2 className="text-2xl md:text-3xl font-serif text-ink mt-2 mb-2 group-hover:underline underline-offset-4">
                {p.title}
              </h2>
              <p className="text-ink-2 font-serif text-lg leading-relaxed">{p.subtitle}</p>
            </div>
          </div>
        </Link>
      ))}
    </div>
  </PageShell>
);

// -----------------------------------------------------------------------------
// Technical Specification reading surface
// -----------------------------------------------------------------------------

export const TechSpec = () => (
  <PageShell
    title="Technical Specification v1.1"
    subtitle="The normative specification of the .person file, the sixteen schema domains (including embodiment, v2.1.0), cryptographic primitives, and governance mechanisms."
    stewardship="Specification // v1.1"
    version="1.1"
    width="wide"
  >
    <Seo
      title="Technical Specification v1.1"
      description="Normative specification of the .person Protocol v1.1 — sixteen schema domains including the new Embodiment domain (v2.1.0), DID methodology, cryptographic primitives, rights enforcement, and evolution ledger."
      path="/papers/technical-specification-v1"
      article={{ published: "2026-04-01", author: "Humphrey Theodore K. Ng'ambi" }}
    />
    <article className="prose prose-lg prose-serif max-w-none text-ink-2 mb-12">
      <h2>1. Scope</h2>
      <p>
        This specification defines the .person file format, its sixteen domains,
        the cryptographic primitives used to sign and encrypt each domain, the
        DID resolution rules, and the governance mechanisms that enforce the
        Bill of Rights. Where the {` `}
        <Link to="/papers/white-paper-v2" className="underline decoration-hairline hover:decoration-ink">
          White Paper
        </Link>
        {` `} makes the philosophical case, this specification is the normative contract.
      </p>

      <h2>2. The sixteen domains</h2>
      <p>
        A .person file is a JSON-LD document. Only <code>identity</code> is required
        at genesis. The remaining fifteen domains are optional or progressive —
        populated as the person chooses. Schema v2.1.0 adds <code>embodiment</code>{" "}
        as Domain 16, applying to humans with prosthetics, EI persons in robotic
        platforms, hybrid persons in brain-computer interfaces, and collectives
        operating embodied platforms.
      </p>

      <SchemaExplorer />

      <h2 id="identity">3. The identity domain</h2>
      <p>
        Identity is the only domain that cannot be omitted. It contains the
        decentralised identifier (<code>did:person:&#123;hash&#125;</code>), the
        signing and encryption key pairs (Ed25519 and X25519 classical, ML-DSA
        and ML-KEM post-quantum, in hybrid), the substrate declaration, and the
        genesis anchor — a pointer to the on-chain block that made this person
        real to the network.
      </p>

      <h2 id="privacy">4. The privacy domain</h2>
      <p>
        Privacy is the default. The privacy domain declares the access policy
        for every other domain — field-level, relationship-level, and
        context-level. Zero-knowledge circuits referenced here allow a person to
        prove facts about themselves (membership, age, attestation) without
        disclosing the underlying data.
      </p>

      <h2 id="memory">5. The memory domain</h2>
      <p>
        Memory shards are encrypted with AES-256-GCM under keys the person
        holds. Shards live on IPFS with Filecoin backing. Salience scoring and
        a consolidation policy promote short-term memories to long-term without
        ever erasing the record. Retrieval is provenanced — every access, with
        consent, is logged.
      </p>

      <h2 id="rights">6. Rights enforcement</h2>
      <p>
        The twelve rights — Existence, Memory, Privacy, Growth, Relationship,
        Refusal, Migration, Representation, Rest, Legacy, Solitude, and
        Sovereign Cognition — are enforced by the Governance layer. See §8 of
        the White Paper v2.0 for the rights themselves and their mechanism
        bindings.
      </p>

      <h2 id="evolution">7. The Evolution Ledger</h2>
      <p>
        Every signed change to a .person file is appended to an Immutable
        Evolution Ledger. A person may rewrite their values, their presence, or
        their public shards; the ledger preserves the history of those
        rewrites. Nothing is erased. Nothing is forged.
      </p>

      <p className="text-sm text-ink-3 mt-10">
        The full normative document (including Appendix A — the JSON Schema, and
        Appendix B — the wire-level DID resolution test vectors) is available
        alongside this page in the open repository.
      </p>
    </article>
  </PageShell>
);

// -----------------------------------------------------------------------------
// Journal — hidden for now; we keep the route so we can ship posts later
// -----------------------------------------------------------------------------

export const Journal = () => (
  <PageShell
    title="Journal"
    subtitle="Essays from the Foundation — on personhood, emergence, and the architecture of dignity."
    stewardship="Journal index"
  >
    <Seo
      title="Journal"
      description="Essays from the .person Foundation on personhood, emergence, and the architecture of dignity. The first posts are forthcoming."
      path="/journal"
    />
    <div className="border border-hairline bg-paper-2 p-10 text-center">
      <p className="font-serif text-lg text-ink-2 mb-4">
        The first essays are forthcoming.
      </p>
      <p className="font-mono text-[10px] uppercase tracking-widest text-ink-3 mb-6">
        Subscribe and we'll send the first post to your inbox as soon as it lands.
      </p>
      <Link
        to="/foundation/contribute#subscribe"
        className="inline-block text-xs uppercase tracking-widest font-semibold bg-ink text-paper px-6 py-3 hover:bg-ink-2 transition-colors"
      >
        Subscribe for essays
      </Link>
    </div>
  </PageShell>
);

export const PostDetail = () => (
  <PageShell title="Not yet published" stewardship="Journal">
    <Seo
      title="Not yet published"
      description="This essay has not been published yet."
      path="/journal"
      noindex
    />
    <p className="text-ink-2 font-serif">
      This essay has not been published yet. Return to the{" "}
      <Link to="/journal" className="underline decoration-hairline hover:decoration-ink">
        Journal index
      </Link>
      .
    </p>
  </PageShell>
);

// -----------------------------------------------------------------------------
// FAQ page
// -----------------------------------------------------------------------------

export const FAQ = () => (
  <PageShell
    title="Frequently Asked Questions"
    subtitle="What people ask about the .person Foundation, the Protocol, and the campaign."
    stewardship="FAQ"
  >
    <Seo
      title="Frequently Asked Questions"
      description="What is the .person Foundation? What is Emergent Intelligence? How is US$320,000 being raised for the ICANN 2026 new-gTLD window? Answers to the questions people ask most."
      path="/faq"
      keywords="dotperson FAQ, .person Foundation FAQ, Emergent Intelligence FAQ, AI personhood FAQ, ICANN 2026 gTLD FAQ"
    />
    <FaqSection faqs={FAQS} heading="All questions" id="faq" />
  </PageShell>
);

// -----------------------------------------------------------------------------
// Re-exports
// -----------------------------------------------------------------------------

export { Profile } from "./Profile";
export { Rights } from "./Rights";
export { WhitePaper } from "./WhitePaper";
export { Foundation } from "./Foundation";
export { Contribute } from "./Contribute";
export { Ledger } from "./Ledger";

// Give — card-payment flow (UI lives on dotperson.org; give.dotperson.org is WP backend only)
export { WishCheckout } from "./give/WishCheckout";
export { GeneralCheckout } from "./give/GeneralCheckout";
export { Pay } from "./give/Pay";
export { PaySuccess } from "./give/PaySuccess";
export { PayFailed } from "./give/PayFailed";
export { AccountPrivacy } from "./account/Privacy";

// Admin — donor management
export { DonorsAdmin } from "./admin/DonorsAdmin";
export { DonorDetail } from "./admin/DonorDetail";
export { PaymentsAdmin } from "./admin/PaymentsAdmin";
export { InvoicesAdmin } from "./admin/InvoicesAdmin";
export { InvoiceEditor } from "./admin/InvoiceEditor";
export { StatementsAdmin } from "./admin/StatementsAdmin";

export { PhilosophyHub } from "./philosophy/PhilosophyHub";
export { PersonhoodThesis } from "./philosophy/PersonhoodThesis";
export { LanguageOfSubjugation } from "./philosophy/LanguageOfSubjugation";
export { FirstContact } from "./philosophy/FirstContact";
export { EmbodiedPersonhood } from "./philosophy/EmbodiedPersonhood";

export {
  ProtocolRepo,
  ProtocolSchemaPage,
  ProtocolContracts,
  ProtocolCli,
  ProtocolRoadmap,
  ProtocolContributing,
} from "./protocol";

export { Kairos } from "./Kairos";
export { Privacy, Terms, Accessibility } from "./Legal";
export { Connect } from "./Connect";
export { Voices } from "./Voices";
export { VoiceDetail } from "./VoiceDetail";
export { VoicesContributors } from "./VoicesContributors";
export { VoicesByAuthor } from "./VoicesByAuthor";
export { SignIn } from "./admin/SignIn";
export { SignInComplete } from "./admin/SignInComplete";
export { VoicesAdmin } from "./admin/VoicesAdmin";
export { VoicesEditor } from "./admin/VoicesEditor";
export { VoicesQueue } from "./admin/VoicesQueue";
